import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { ImageWidget } from "../../dashboards/pictures/ImageWidget";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError } from "../../../api/problem";
import { durationMs } from "../../../widgets/charts/series";
import { heatmapFrom } from "../../../widgets/components/Heatmap";
import { localMidnight } from "../../../widgets/glance";
import { WidgetView } from "../../../widgets/registry";
import { Frame, Note } from "../../../widgets/shared";
import type { WidgetAction } from "../../../widgets/types";
import type { HistorySeries } from "../../../widgets/generated/widget-fixture";
import type { ChartRange, Widget, WidgetInput } from "../../../widgets/types";
import { historyLimitText, shownHistoryDays } from "../plans/plans";
import { PlansDialog } from "../plans/PlansDialog";
import { devicePlansQuery } from "../plans/queries";
import { CounterPeriodsDialog } from "./CounterPeriods";
import { DashboardWidget } from "../../embed/DashboardWidget";
import { DeviceSheet } from "./DeviceSheet";
import {
  countersQuery,
  deviceQuery,
  historyQuery,
  latestQuery,
  resetCounter,
  statisticsMonthsQuery,
  statisticsPeriodsQuery,
  type HistoryParams,
} from "./queries";

const CHARTS = new Set(["line_chart", "bar_chart"]);
/** The widgets of widget-library-2 that draw a range of history (WLB-11 to WLB-14). */
const GLANCE = new Set(["sparkline", "stats", "state_timeline", "heatmap"]);
/** About as many points as a chart is wide; the server picks the resolution from it (WEB-F-DEV-04). */
const CHART_POINTS = 600;
const MINUTE = 60_000;
const AGGREGATES = new Set(["avg", "min", "max", "sum", "last"]);
/** The Pro statistics widgets (pro-statistics §2): they read the statistics routes, not the history. */
const STATISTICS = new Set(["period_totals", "period_compare", "year_compare"]);
const COMPARED = new Set(["week", "month", "year"]);

export interface HistoryRequest {
  device: string;
  params: HistoryParams;
}

/**
 * The history requests for a chart at a range, one per datapoint binding (a chart binds up to eight). The end is
 * rounded to the minute so the cache keys hold still.
 */
export function historyRequests(widget: Widget, range: ChartRange, now: Date): HistoryRequest[] {
  const cfg = widget.config ?? {};
  const bucket = typeof cfg.bucket === "string" ? cfg.bucket : undefined;
  // The dashboard contract's defaults: sum for bar_chart; avg for line_chart, which is also the API's own default.
  const aggregate = AGGREGATES.has(cfg.aggregate as string)
    ? (cfg.aggregate as HistoryParams["aggregate"])
    : widget.type === "bar_chart"
      ? "sum"
      : undefined;
  let from: string;
  let to: string;
  if (typeof range === "string") {
    const end = Math.floor(now.getTime() / MINUTE) * MINUTE;
    to = new Date(end).toISOString();
    from = new Date(end - (durationMs(range) ?? 86_400_000)).toISOString();
  } else ({ from, to } = range);
  // A zoomed range drops the bucket so the server can answer finer than the widget's own bucket.
  const zoomed = typeof range !== "string";
  return (widget.bindings ?? []).flatMap((b) =>
    b.datapoint
      ? [
          {
            device: b.device,
            params: {
              datapoint: b.datapoint,
              from,
              to,
              ...(bucket && !zoomed ? { bucket } : { points: CHART_POINTS }),
              ...(aggregate ? { aggregate } : {}),
            },
          },
        ]
      : [],
  );
}

/**
 * What a sparkline, stats, on/off timeline or heatmap reads (WLB-11 to WLB-14), one request per datapoint binding:
 * averages for the first two, the last value per bucket (raw when the server can) for a timeline, hours for a heatmap.
 * The end is rounded to the minute so the cache keys hold still.
 */
export function glanceRequests(widget: Widget, now: Date): HistoryRequest[] {
  const cfg = widget.config ?? {};
  const end = Math.floor(now.getTime() / MINUTE) * MINUTE;
  const range = typeof cfg.range === "string" ? cfg.range : undefined;
  const span = (fallback: string) => durationMs(range ?? fallback) ?? 86_400_000;
  let from: number;
  let shape: Omit<HistoryParams, "datapoint" | "from" | "to">;
  switch (widget.type) {
    case "sparkline":
      from = end - span("24h");
      shape = { points: 120, aggregate: "avg" };
      break;
    case "stats":
      from = range === "today" ? localMidnight(end, undefined) : end - span("24h");
      shape = { points: 96, aggregate: "avg" };
      break;
    case "state_timeline":
      from = end - span("24h");
      shape = { points: 2000, aggregate: "last" };
      break;
    default:
      from = heatmapFrom(range, new Date(end));
      shape = {
        bucket: "1h",
        aggregate: AGGREGATES.has(cfg.aggregate as string) ? (cfg.aggregate as HistoryParams["aggregate"]) : "avg",
      };
  }
  return (widget.bindings ?? []).flatMap((b) =>
    b.datapoint
      ? [
          {
            device: b.device,
            params: {
              datapoint: b.datapoint,
              from: new Date(from).toISOString(),
              to: new Date(end).toISOString(),
              ...shape,
            },
          },
        ]
      : [],
  );
}

/**
 * A widget on the device page; a chart reads its own history at the range its picker or zoom asks for, and a map
 * opens a device's sheet from its markers and list. Controls work only with `onAction` (device-controls WEB-CMD-01).
 */
export function PageWidget({
  widget,
  input,
  now,
  orgId,
  onAction,
  busy = false,
  preview = false,
}: {
  widget: Widget;
  input: WidgetInput;
  now: Date;
  orgId: string;
  onAction?: (action: WidgetAction) => void;
  /** In an editor: a chart draws its configured range without its own range buttons (the editor has the picker). */
  preview?: boolean;
  /** Its action is in flight, so it cannot be used again yet (a scene button, WEB-SCN-04). */
  busy?: boolean;
}) {
  if (CHARTS.has(widget.type))
    return <ChartWidget widget={widget} input={input} now={now} orgId={orgId} preview={preview} />;
  if (widget.type === "map") return <MapPageWidget widget={widget} input={input} now={now} orgId={orgId} />;
  if (widget.type === "image") return <ImageWidget widget={widget} orgId={orgId} />;
  if (widget.type === "device_tile")
    return <TileWidget widget={widget} input={input} now={now} orgId={orgId} preview={preview} />;
  if (GLANCE.has(widget.type)) return <GlanceWidget widget={widget} input={input} now={now} orgId={orgId} />;
  if (STATISTICS.has(widget.type))
    return <StatisticsPageWidget widget={widget} input={input} now={now} orgId={orgId} />;
  if (widget.type === "weather_station")
    return <StationPageWidget widget={widget} input={input} now={now} orgId={orgId} />;
  if (widget.type === "reset_counter")
    return <CounterPageWidget widget={widget} input={input} now={now} orgId={orgId} preview={preview} />;
  if (widget.type === "health")
    return <HealthWidget widget={widget} input={input} now={now} orgId={orgId} preview={preview} />;
  if (widget.type === "web_widget") return <WebWidget widget={widget} orgId={orgId} />;
  return <WidgetView widget={widget} input={input} now={now} readOnly={!onAction || busy} onAction={onAction} />;
}

/**
 * The input with each request's history, or, while a new range is read, the last history read for that datapoint.
 * useQueries gives a changed key a new observer, so placeholderData has nothing to keep, and a chart went blank for
 * as long as the request took after every pan or zoom (owner's report #39). The API types points as unknown[][]; the
 * chart reads each entry defensively (toChartSeries).
 */
function useKeptHistory(input: WidgetInput, requests: HistoryRequest[], histories: { data?: unknown }[]): WidgetInput {
  const [kept, setKept] = useState<Record<string, HistorySeries>>({});
  const keys = requests.map((r) => `${r.device}/${r.params.datapoint}`);
  const fresh = keys.flatMap((k, i) => {
    const data = histories[i]?.data as HistorySeries | undefined;
    return data && kept[k] !== data ? [[k, data] as const] : [];
  });
  // React's pattern for state derived from the previous render: set it while rendering.
  if (fresh.length) setKept({ ...kept, ...Object.fromEntries(fresh) });
  const read = keys.flatMap((k, i) => {
    const data = (histories[i]?.data as HistorySeries | undefined) ?? kept[k];
    return data ? [[k, data] as const] : [];
  });
  return read.length ? { ...input, history: { ...input.history, ...Object.fromEntries(read) } } : input;
}

function ChartWidget({
  widget,
  input,
  now,
  orgId,
  preview,
}: {
  widget: Widget;
  input: WidgetInput;
  now: Date;
  orgId: string;
  preview: boolean;
}) {
  const configured = typeof widget.config?.range === "string" ? widget.config.range : "24h";
  const [picked, setRange] = useState<ChartRange>(configured);
  const range = preview ? configured : picked;
  const requests = historyRequests(widget, range, now);
  // Without an organisation (the console's device type preview, WEB-DTP-04) nothing is read.
  const histories = useQueries({
    queries: requests.map((r) => ({ ...historyQuery(orgId, r.device, r.params), enabled: !!orgId })),
  });
  // The plan's history window (plan-limits PL-41): longer presets are disabled. The device page has these cached.
  const devices = useQueries({
    queries: [...new Set(requests.map((r) => r.device))].map((id) => ({
      ...deviceQuery(orgId, id),
      staleTime: 60_000,
      enabled: !!orgId,
    })),
  });
  const days = shownHistoryDays(devices.map((d) => d.data));
  const { t } = useTranslation();
  // The device's plan shows no history (vision-api 0.21.0 403 plan_required): say so instead of an empty chart
  // (WEB-DDB-EDIT-02, DDB-23). One device with history among several still draws its lines.
  const planless =
    histories.length > 0 &&
    histories.every((h) => h.error instanceof ApiError && h.error.status === 403 && h.error.code === "plan_required");
  const withHistory = useKeptHistory(input, requests, histories);
  if (planless) {
    return (
      <Frame widget={widget} state="empty" title={widget.title}>
        <Note>{t("devicePage.historyNeedsPlan")}</Note>
      </Frame>
    );
  }
  return (
    <WidgetView
      widget={widget}
      input={withHistory}
      now={now}
      readOnly
      range={range}
      onRange={preview ? undefined : setRange}
      maxRangeMs={days === undefined ? undefined : days * 86_400_000}
      rangeNote={historyLimitText(days, t)}
    />
  );
}

function MapPageWidget({
  widget,
  input,
  now,
  orgId,
}: {
  widget: Widget;
  input: WidgetInput;
  now: Date;
  orgId: string;
}) {
  const [open, setOpen] = useState<string | undefined>();
  return (
    <>
      <WidgetView widget={widget} input={input} now={now} readOnly onOpenDevice={orgId ? setOpen : undefined} />
      <DeviceSheet
        orgId={orgId}
        deviceId={open}
        name={open ? input.devices?.[open]?.name : undefined}
        now={now}
        onClose={() => setOpen(undefined)}
      />
    </>
  );
}

/** A device tile reads the last day of its first value for the trend line, and opens its device (DTP-11). */
function TileWidget({
  widget,
  input,
  now,
  orgId,
  preview,
}: {
  widget: Widget;
  input: WidgetInput;
  now: Date;
  orgId: string;
  preview: boolean;
}) {
  const navigate = useNavigate();
  const first = widget.config?.trend === true ? historyRequests(widget, "24h", now)[0] : undefined;
  const history = useQuery({
    ...historyQuery(orgId, first?.device ?? "", {
      ...(first?.params ?? { datapoint: "", from: "", to: "" }),
      points: 48,
    }),
    enabled: !!first && !!orgId,
  });
  const withHistory: WidgetInput =
    first && history.data
      ? {
          ...input,
          history: { ...input.history, [`${first.device}/${first.params.datapoint}`]: history.data as HistorySeries },
        }
      : input;
  return (
    <WidgetView
      widget={widget}
      input={withHistory}
      now={now}
      readOnly
      onOpenDevice={
        preview ? undefined : (deviceId) => void navigate({ to: "/devices/$deviceId", params: { deviceId } })
      }
    />
  );
}

/** A sparkline, stats, on/off timeline or heatmap with the history it draws, or "History needs a plan" (WLB-03). */
function GlanceWidget({ widget, input, now, orgId }: { widget: Widget; input: WidgetInput; now: Date; orgId: string }) {
  const { t } = useTranslation();
  const requests = glanceRequests(widget, now);
  const histories = useQueries({ queries: requests.map((r) => historyQuery(orgId, r.device, r.params)) });
  const planless =
    histories.length > 0 &&
    histories.every((h) => h.error instanceof ApiError && h.error.status === 403 && h.error.code === "plan_required");
  const withHistory = useKeptHistory(input, requests, histories);
  // A trend still shows the current value without its history.
  if (planless && widget.type !== "sparkline") {
    return (
      <Frame widget={widget} state="empty" title={widget.title}>
        <Note>{t("devicePage.historyNeedsPlan")}</Note>
      </Frame>
    );
  }
  return (
    <>
      <WidgetView widget={widget} input={withHistory} now={now} readOnly />
      {planless && <span className="sr-only">{t("devicePage.historyNeedsPlan")}</span>}
    </>
  );
}

/**
 * Device health reads each member's signal and battery (WLB-15). The members' pages are not loaded, so a negative
 * signal is taken as dBm and anything else as %; the battery is in %.
 */
function HealthWidget({
  widget,
  input,
  now,
  orgId,
  preview,
}: {
  widget: Widget;
  input: WidgetInput;
  now: Date;
  orgId: string;
  preview: boolean;
}) {
  const navigate = useNavigate();
  const cfg = widget.config ?? {};
  const signalKey = typeof cfg.signal === "string" ? cfg.signal : "signal";
  const batteryKey = typeof cfg.battery === "string" ? cfg.battery : "battery_percent";
  const members = (input.members ?? []).slice(0, 200);
  const latest = useQuery(
    latestQuery(
      orgId,
      members.flatMap((device) => [signalKey, batteryKey].map((datapoint) => ({ device, datapoint }))),
    ),
  );
  const points: NonNullable<WidgetInput["points"]> = { ...input.points };
  for (const [k, v] of Object.entries(latest.data?.points ?? {})) {
    if (!v || typeof v.value !== "number") continue;
    const signal = k.endsWith(`/${signalKey}`);
    points[k] = {
      name: signal ? signalKey : batteryKey,
      kind: "number",
      unit: signal ? (v.value < 0 ? "dBm" : "%") : "%",
      decimals: 0,
      ...(signal ? {} : { range: [0, 100] }),
      latest: { value: v.value, at: v.at },
    };
  }
  return (
    <WidgetView
      widget={widget}
      input={{ ...input, points }}
      now={now}
      readOnly
      onOpenDevice={
        preview ? undefined : (deviceId) => void navigate({ to: "/devices/$deviceId", params: { deviceId } })
      }
    />
  );
}

type StatisticsAnswer = NonNullable<WidgetInput["statistics"]>[string];

/** What the statistics routes refused with, as the widget input's problem (PST-04, PST-13); other errors draw nothing. */
export function statisticsProblem(error: unknown): StatisticsAnswer["problem"] {
  if (!(error instanceof ApiError)) return undefined;
  if (error.status === 403 && error.code === "plan_feature")
    return {
      status: 403,
      code: "plan_feature",
      feature: "statistics",
      plan: error.problem?.plan as string | undefined,
    };
  if (error.status === 422 && error.code === "outside_history") return { status: 422, code: "outside_history" };
  return undefined;
}

/**
 * A totals, comparison or year chart with its numbers, read when shown, every 5 minutes, and again when the live
 * socket brings a new reading of the value, at most once a minute (PST-33). A device whose plan has no statistics
 * shows the placeholder, with See plans for the Owner and Admins (PST-04, DP-13).
 */
function StatisticsPageWidget({
  widget,
  input,
  now,
  orgId,
}: {
  widget: Widget;
  input: WidgetInput;
  now: Date;
  orgId: string;
}) {
  const b = (widget.bindings ?? []).find((x) => x.datapoint);
  const device = b?.device ?? "";
  const datapoint = b?.datapoint ?? "";
  const period = COMPARED.has(widget.config?.period as string)
    ? (widget.config?.period as "week" | "month" | "year")
    : "month";
  const months = widget.type === "year_compare";
  const enabled = !!orgId && !!b;
  const periods = useQuery({
    ...statisticsPeriodsQuery(orgId, device, datapoint, widget.type === "period_compare" ? period : undefined),
    enabled: enabled && !months,
  });
  const year = useQuery({ ...statisticsMonthsQuery(orgId, device, datapoint), enabled: enabled && months });
  const q = months ? year : periods;
  useRefetchOnReading(input.points?.[`${device}/${datapoint}`]?.latest?.at, q);
  const problem = statisticsProblem(q.error);
  const sheet = usePlansSheet(orgId, device, !!problem, problem?.code === "plan_feature");
  const answer: StatisticsAnswer | undefined = problem
    ? { problem }
    : months
      ? year.data && { months: year.data.years, aggregate: year.data.aggregate }
      : periods.data && { periods: periods.data.periods };
  const withStatistics: WidgetInput = answer
    ? {
        ...input,
        statistics: { ...input.statistics, [`${device}/${datapoint}`]: answer },
        can_change_plan: sheet.canChangePlan,
      }
    : input;
  return (
    <>
      <WidgetView widget={widget} input={withStatistics} now={now} readOnly onSeePlans={sheet.onSeePlans} />
      {sheet.dialog}
    </>
  );
}

/**
 * The plans sheet behind a Pro placeholder's See plans (PST-04, DP-13): whether the viewer may change the device's
 * plan, and the dialog. `needed` reads the device; `locked` also reads its plans.
 */
function usePlansSheet(orgId: string, device: string, needed: boolean, locked: boolean) {
  const plans = useQuery({ ...devicePlansQuery(orgId, device), enabled: !!orgId && locked });
  const dev = useQuery({ ...deviceQuery(orgId, device), staleTime: 60_000, enabled: !!orgId && needed });
  const [open, setOpen] = useState(false);
  return {
    canChangePlan: (plans.data ?? []).some((p) => p.can_request),
    onSeePlans: dev.data ? () => setOpen(true) : undefined,
    dialog: open && dev.data && <PlansDialog orgId={orgId} device={dev.data} onClose={() => setOpen(false)} />,
  };
}

/** Reads a query again when a new reading of its value arrives, at most once a minute (PST-33, WSW-22). */
function useRefetchOnReading(latestAt: string | undefined, q: { refetch: () => unknown; dataUpdatedAt: number }) {
  const { refetch, dataUpdatedAt } = q;
  useEffect(() => {
    if (latestAt && dataUpdatedAt && Date.now() - dataUpdatedAt > 60_000) void refetch();
  }, [latestAt, dataUpdatedAt, refetch]);
}

/**
 * The weather station with rain today from the statistics periods route (WSW-02), read like the statistics widgets.
 * A device whose plan has no statistics draws the placeholder (WSW-20) without asking.
 */
function StationPageWidget({
  widget,
  input,
  now,
  orgId,
}: {
  widget: Widget;
  input: WidgetInput;
  now: Date;
  orgId: string;
}) {
  const device = widget.source?.devices?.[0] ?? "";
  const rain = Object.entries(input.points ?? {}).find(
    ([k, p]) => k.startsWith(`${device}/`) && p.station_slot === "rain",
  );
  const datapoint = rain ? rain[0].slice(device.length + 1) : "";
  const features = input.devices?.[device]?.features;
  const locked = !!features && !features.includes("statistics");
  const periods = useQuery({
    ...statisticsPeriodsQuery(orgId, device, datapoint),
    enabled: !!orgId && !!datapoint && !locked,
  });
  useRefetchOnReading(rain?.[1].latest?.at, periods);
  const problem = statisticsProblem(periods.error);
  const sheet = usePlansSheet(orgId, device, locked || !!problem, locked || problem?.code === "plan_feature");
  const answer: StatisticsAnswer | undefined = problem
    ? { problem }
    : periods.data && { periods: periods.data.periods };
  const withRain: WidgetInput = {
    ...input,
    ...(answer ? { statistics: { ...input.statistics, [`${device}/${datapoint}`]: answer } } : {}),
    can_change_plan: sheet.canChangePlan,
  };
  return (
    <>
      <WidgetView widget={widget} input={withRain} now={now} readOnly onSeePlans={sheet.onSeePlans} />
      {sheet.dialog}
    </>
  );
}

/**
 * A reset counter with its device's counter, read on open, every 5 minutes and on a new reading (WSW-22). Reset sends
 * the start the widget showed, so a reset someone else made first is not repeated: the widget says so and reads the
 * counter again (WSW-12). Earlier lists the periods (WSW-14).
 */
function CounterPageWidget({
  widget,
  input,
  now,
  orgId,
  preview,
}: {
  widget: Widget;
  input: WidgetInput;
  now: Date;
  orgId: string;
  preview: boolean;
}) {
  const { t } = useTranslation("widget");
  const client = useQueryClient();
  const b = (widget.bindings ?? []).find((x) => x.datapoint);
  const device = b?.device ?? "";
  const datapoint = b?.datapoint ?? "";
  const k = `${device}/${datapoint}`;
  const counters = useQuery({ ...countersQuery(orgId, device), enabled: !!orgId && !!b });
  useRefetchOnReading(input.points?.[k]?.latest?.at, counters);
  const [note, setNote] = useState<string>();
  const [earlier, setEarlier] = useState(false);
  const problem = statisticsProblem(counters.error);
  const locked = problem?.code === "plan_feature";
  const sheet = usePlansSheet(orgId, device, locked, locked);
  const counter = counters.data?.counters.find((c) => c.datapoint === datapoint);
  const answer: NonNullable<WidgetInput["counters"]>[string] | undefined = locked
    ? { problem: { status: 403, code: "plan_feature", feature: "statistics" } }
    : counter && { counter };
  const withCounter: WidgetInput = answer
    ? { ...input, counters: { ...input.counters, [k]: answer }, can_change_plan: sheet.canChangePlan }
    : input;
  const refresh = () => {
    void client.invalidateQueries({ queryKey: countersQuery(orgId, device).queryKey });
    void client.invalidateQueries({
      queryKey: ["org", orgId, "/v1/devices/{device_id}/counters/{datapoint}/periods", device],
    });
  };
  const onReset = async (since: string) => {
    setNote(undefined);
    try {
      await resetCounter(orgId, device, datapoint, since);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) setNote(t("counter.changed"));
      else throw e;
    } finally {
      refresh();
    }
  };
  const point = input.points?.[k];
  return (
    <>
      <WidgetView
        widget={widget}
        input={withCounter}
        now={now}
        readOnly={preview}
        onSeePlans={sheet.onSeePlans}
        onCounterReset={preview ? undefined : (since) => void onReset(since)}
        onCounterEarlier={preview ? undefined : () => setEarlier(true)}
        counterNote={note}
      />
      {sheet.dialog}
      {earlier && point && (
        <CounterPeriodsDialog
          orgId={orgId}
          device={device}
          datapoint={datapoint}
          title={widget.title ?? point.name}
          decimals={point.decimals}
          now={now}
          onClose={() => setEarlier(false)}
        />
      )}
    </>
  );
}

/**
 * A web_widget saved on a dashboard (mobile-dashboards-2 MD2-03): the phones draw it in a web view; the web draws the
 * widget it names directly.
 */
function WebWidget({ widget, orgId }: { widget: Widget; orgId: string }) {
  const config = (widget.config ?? {}) as { dashboard_id?: unknown; widget_id?: unknown };
  if (typeof config.dashboard_id !== "string" || typeof config.widget_id !== "string" || !orgId) return null;
  return (
    <DashboardWidget orgId={orgId} dashboardId={config.dashboard_id} widgetId={config.widget_id} title={widget.title} />
  );
}
